<template>
  <div class="login-page">
    <DefaultHeader
      title="Вход в личный аккаунт"
      subtitle="Некоторые типы входа могут быть не доступны в вашем регионе"
      class="login-header"
    />

    <div class="login-background">
      <img class="login-logo" />
    </div>

    <main class="login-content">
      <LoginButtonContainer
        :showForgotPassword="showForgotPassword"
        @GoogleSignInButtonPressed="handleGoogleSignIn"
        @AppleSignInButtonPressed="handleAppleSignIn"
        @PhoneNumberSignInPressed="handlePhoneNumberSignIn"
        @EmailSignInPressed="handleEmailSignIn"
        @ForgotPasswordPressed="handleForgotPassword"
        @GoToRegister="goToRegister"
      />
    </main>

    <transition name="fade">
      <div
        v-if="isLoading"
        class="loading-overlay"
      >
        <div class="loading-container">
          <div class="spinner"></div>

          <p class="loading-text">
            {{ loadingText }}
          </p>
        </div>
      </div>
    </transition>

    <div
      v-if="errorMessage"
      class="error-modal"
    >
      <div class="error-content">
        <p class="error-text">
          {{ errorMessage }}
        </p>

        <button
          class="error-btn"
          @click="errorMessage = ''"
        >
          ОК
        </button>
      </div>
    </div>

    <ErrorToast
      v-if="showError"
      :message="errorMessage"
      @click="showError = false"
    />

    <WarningToast
      v-if="showWarning"
      :message="warningMessage"
    />

    <ElegantErrorDialog
      :visible="showErr"
      :title="errTitle"
      :message="errMessage"
      :type="errType"
      :autoCloseMs="3500"
      @close="showErr = false"
    />

    <Footer class="login-footer" />
  </div>
</template>

<script setup>
import LoginButtonContainer from "../components/login/LoginButtonContainer.vue";
import ElegantErrorDialog from "../components/errors/ElegantErrorDialog.vue";

import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword
} from "firebase/auth";

import { useRouter } from "vue-router";
import { ref, onMounted } from "vue";

import DefaultHeader from "../components/LoginHeader.vue";
import UserService from "../api/UserService.js";
import Footer from "../components/MiniFooter.vue";
import AppleSignInService from "../api/AppleSignInService.js";
import GoogleSignInService from "../api/GoogleSignInService.js";
import UserAuthService from "../api/UserAuthService.js";
import ErrorToast from "../components/ErrorToast.vue";
import WarningToast from "../components/WarningToast.vue";

const router = useRouter();

const warningMessage = ref("");
const showError = ref(false);
const showWarning = ref(false);
const isLightTheme = ref(false);
const showErr = ref(false);

const errTitle = ref(
  "Неверный пароль"
);

const errMessage = ref(
  "Проверьте раскладку, Caps Lock и попробуйте ещё раз."
);

const showForgotPassword = ref(false);

const loadingText = ref(
  "Вход в аккаунт..."
);

const errType = ref("error");

const isLoading = ref(false);
const errorMessage = ref("");

const RESET_PASSWORD_URL =
  "https://europe-west3-hyper-e122e.cloudfunctions.net/reset_password";

/* ========================================================= */
/* AUTH STATE */
/* ========================================================= */

onMounted(() => {
  const auth = getAuth();

  onAuthStateChanged(
    auth,
    async user => {
      console.log(
        "🔥 AUTH STATE:",
        user
      );

      if (!user) {
        console.log(
          "❌ user = null"
        );

        return;
      }

      console.log(
        "✅ USER:",
        user.uid
      );

      console.log(
        "✅ USER LOGGED IN:",
        user.uid
      );

      isLoading.value = true;

      try {
        const userData =
          await UserAuthService
            .checkUserStatus(
              user.uid,
              3
            );

        if (!userData) {
          errorMessage.value =
            "Ошибка при проверке пользователя";

          showError.value =
            true;

          return;
        }

        if (
          userData
            .isRegistrationComplete
        ) {
          console.log(
            "➡️ /feed"
          );

          await router.push(
            "/feed"
          );
        } else {
          console.log(
            "➡️ /registration"
          );

          await router.push(
            "/registration"
          );
        }
      } catch (e) {
        console.error(
          "❌ checkUserStatus error:",
          e
        );

        await router.push(
          "/registration"
        );
      } finally {
        isLoading.value =
          false;
      }
    }
  );
});

/* ========================================================= */
/* REGISTER */
/* ========================================================= */

const goToRegister = () => {
  router.push(
    "/register-email"
  );
};

/* ========================================================= */
/* GOOGLE */
/* ========================================================= */

const handleGoogleSignIn =
  async () => {
    isLoading.value = true;

    try {
      const response =
        await GoogleSignInService
          .signInWithGoogle();

      if (!response.success) {
        errorMessage.value =
          "Ошибка авторизации через Google. Попробуйте снова.";

        showError.value =
          true;

        return;
      }

      const userData =
        await UserAuthService
          .checkUserStatus(
            response.uid,
            3
          );

      await routeAfterLogin(
        userData
      );
    } catch (err) {
      console.error(
        "❌ Google login error:",
        err
      );

      errorMessage.value =
        "Произошла непредвиденная ошибка. Попробуйте позже.";

      showError.value = true;
    } finally {
      isLoading.value = false;
    }
  };

/* ========================================================= */
/* APPLE */
/* ========================================================= */

const handleAppleSignIn =
  async () => {
    isLoading.value = true;

    try {
      const response =
        await AppleSignInService
          .signInWithApple();

      if (!response.success) {
        errorMessage.value =
          "Ошибка входа через Apple";

        showError.value =
          true;

        return;
      }

      const userData =
        await UserAuthService
          .checkUserStatus(
            response.userId,
            4
          );

      await routeAfterLogin(
        userData
      );
    } catch (err) {
      console.error(
        "❌ Apple login error:",
        err
      );

      errorMessage.value =
        "Ошибка авторизации через Apple";

      showError.value = true;
    } finally {
      isLoading.value = false;
    }
  };

/* ========================================================= */
/* PHONE */
/* ========================================================= */

const handlePhoneNumberSignIn =
  () => {
    router.push(
      "/register-phone-auth"
    );
  };

/* ========================================================= */
/* BACK */
/* ========================================================= */

const goBack = () => {
  if (
    window.history.length > 1
  ) {
    router.back();
  } else {
    router.push("/");
  }
};

/* ========================================================= */
/* EMAIL */
/* ========================================================= */

const handleEmailSignIn =
  async ({
    email,
    password
  }) => {
    isLoading.value =
      true;

    loadingText.value =
      "Вход в аккаунт...";

    showForgotPassword.value =
      false;

    const normalizedEmail =
      String(
        email || ""
      )
        .trim()
        .toLowerCase();

    if (
      !normalizedEmail ||
      !password
    ) {
      errorMessage.value =
        "Email и пароль обязательны!";

      showError.value =
        true;

      isLoading.value =
        false;

      return;
    }

    try {
      const auth =
        getAuth();

      const userCredential =
        await signInWithEmailAndPassword(
          auth,
          normalizedEmail,
          password
        );

      const user =
        userCredential.user;

      const token =
        await user.getIdToken(
          true
        );

      localStorage.setItem(
        "auth_token",
        token
      );

      localStorage.setItem(
        "user_id",
        user.uid
      );

      let userData = null;

      try {
        userData =
          await UserAuthService
            .checkUserStatus(
              user.uid,
              5
            );
      } catch (error) {
        console.warn(
          "⚠️ checkUserStatus упал, " +
          "направляем на регистрацию",
          error
        );

        await router.push(
          "/registration"
        );

        return;
      }

      if (
        userData
          ?.isRegistrationComplete
      ) {
        try {
          await UserService
            .getProfile(
              user.uid
            );
        } catch (error) {
          warningMessage.value =
            "Не удалось получить профиль. " +
            "Продолжаем вход.";

          showWarning.value =
            true;
        }

        await router.push(
          "/feed"
        );
      } else {
        await router.push(
          "/registration"
        );
      }
    } catch (error) {
      console.error(
        "❌ Ошибка входа по email:",
        error
      );

      const credentialErrorCodes =
        [
          "auth/wrong-password",
          "auth/invalid-credential",
          "auth/user-not-found"
        ];

      if (
        credentialErrorCodes
          .includes(
            error?.code
          )
      ) {
        errType.value =
          "error";

        errTitle.value =
          "Неверные данные";

        errMessage.value =
          "Проверьте email, раскладку, " +
          "Caps Lock и пароль.";

        showForgotPassword.value =
          true;
      } else if (
        error?.code ===
        "auth/invalid-email"
      ) {
        errType.value =
          "error";

        errTitle.value =
          "Некорректный email";

        errMessage.value =
          "Проверьте правильность email.";
      } else if (
        error?.code ===
        "auth/too-many-requests"
      ) {
        errType.value =
          "error";

        errTitle.value =
          "Слишком много попыток";

        errMessage.value =
          "Попробуйте войти немного позже.";

        showForgotPassword.value =
          true;
      } else {
        errType.value =
          "error";

        errTitle.value =
          "Ошибка авторизации";

        errMessage.value =
          "Что-то пошло не так. " +
          "Попробуйте позже.";
      }

      showErr.value = true;
    } finally {
      isLoading.value =
        false;
    }
  };

/* ========================================================= */
/* FORGOT PASSWORD */
/* ========================================================= */

const handleForgotPassword =
  async ({
    email
  }) => {
    const normalizedEmail =
      String(
        email || ""
      )
        .trim()
        .toLowerCase();

    if (
      !normalizedEmail ||
      !normalizedEmail
        .includes("@")
    ) {
      errType.value =
        "error";

      errTitle.value =
        "Введите email";

      errMessage.value =
        "Укажите корректный email, " +
        "чтобы восстановить пароль.";

      showErr.value = true;

      return;
    }

    isLoading.value =
      true;

    loadingText.value =
      "Отправляем письмо...";

    try {
      console.log(
        "📨 Запрос восстановления:",
        normalizedEmail
      );

      const response =
        await fetch(
          RESET_PASSWORD_URL,
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json"
            },

            body:
              JSON.stringify({
                email:
                  normalizedEmail
              })
          }
        );

      let result = {};

      try {
        result =
          await response.json();
      } catch {
        result = {};
      }

      if (!response.ok) {
        throw new Error(
          result.message ||
          result.error ||
          "Не удалось отправить письмо"
        );
      }

      errType.value =
        "success";

      errTitle.value =
        "Письмо отправлено";

      errMessage.value =
        "Если аккаунт с таким email существует, " +
        "мы отправили ссылку восстановления.";

      showErr.value = true;

      showForgotPassword.value =
        false;

      console.log(
        "✅ Письмо восстановления отправлено"
      );
    } catch (error) {
      console.error(
        "❌ Ошибка восстановления пароля:",
        error
      );

      errType.value =
        "error";

      errTitle.value =
        "Не удалось отправить";

      errMessage.value =
        "Попробуйте повторить запрос позже.";

      showErr.value = true;
    } finally {
      isLoading.value =
        false;

      loadingText.value =
        "Вход в аккаунт...";
    }
  };

/* ========================================================= */
/* LOGIN ROUTING */
/* ========================================================= */

const routeAfterLogin =
  async userData => {
    if (!userData) {
      errorMessage.value =
        "Ошибка при проверке пользователя";

      showError.value =
        true;

      return;
    }

    /* ===================================================== */
    /* FROZEN ACCOUNT */
    /* ===================================================== */

    if (
      userData.isFrozen ===
      true
    ) {
      console.log(
        "🧊 Аккаунт заморожен → /account-is-frozen"
      );

      await router.replace(
        "/account-is-frozen"
      );

      return;
    }

    /* ===================================================== */
    /* ACTIVE ACCOUNT */
    /* ===================================================== */

    if (
      userData
        .isRegistrationComplete
    ) {
      console.log(
        "➡️ /feed"
      );

      await router.replace(
        "/feed"
      );

      return;
    }

    /* ===================================================== */
    /* REGISTRATION */
    /* ===================================================== */

    console.log(
      "➡️ /registration"
    );

    await router.replace(
      "/registration"
    );
  };
</script>

<style scoped>
* {
  box-sizing: border-box;
}

/* ========================================================= */
/* PAGE */
/* ========================================================= */

.login-page {
  position: relative;

  width: 100%;

  height: 100vh;
  height: 100dvh;

  min-height: 100vh;
  min-height: 100dvh;

  display: grid;

  grid-template-rows:
    auto
    minmax(0, 1fr)
    auto;

  overflow: hidden;

  background-color:
    var(--bg);
}

/* ========================================================= */
/* BACKGROUND */
/* ========================================================= */

.login-background {
  position: absolute;

  inset: 0;

  z-index: 0;

  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  display: flex;

  justify-content: center;
  align-items: flex-start;

  padding-top: 20px;

  pointer-events: none;
}

[data-theme="dark"]
.login-background {
  background-image:
    url("../assets/login_header_dark.webp");
}

[data-theme="light"]
.login-background {
  background-image:
    url("../assets/login_header_light.webp");
}

.login-background::after {
  content: "";

  position: absolute;

  inset: 0;

  background:
    linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.05),
      rgba(0, 0, 0, 0.18)
    );

  pointer-events: none;
}

/* ========================================================= */
/* LOGO */
/* ========================================================= */

.login-logo {
  position: relative;

  z-index: 1;

  width: 180px;
  height: auto;

  max-width: 100%;

  transform:
    translateY(50px);

  content:
    url("../assets/login_logo_dark.png");
}

[data-theme="light"]
.login-logo {
  content:
    url("../assets/login_logo_light.png");
}

/* ========================================================= */
/* HEADER */
/* ========================================================= */

.login-header {
  position: relative;

  z-index: 10;

  width: 100%;

  min-width: 0;

  flex-shrink: 0;

  backdrop-filter:
    blur(20px);

  -webkit-backdrop-filter:
    blur(20px);

  border-top:
    1px solid
    rgba(
      255,
      255,
      255,
      0.08
    );
}

/* ========================================================= */
/* CONTENT */
/* ========================================================= */

.login-content {
  position: relative;

  z-index: 2;

  width: 100%;
  min-width: 0;
  min-height: 0;

  display: flex;

  justify-content: center;
  align-items: flex-start;

  overflow-y: auto;
  overflow-x: hidden;

  overscroll-behavior:
    contain;

  padding-top:
    clamp(
      60px,
      9vh,
      140px
    );

  padding-bottom: 24px;

  scrollbar-width: none;
}

.login-content::-webkit-scrollbar {
  display: none;
}

/* ========================================================= */
/* FOOTER */
/* ========================================================= */

.login-footer {
  position: relative;

  z-index: 10;

  width: 100%;

  min-width: 0;

  flex-shrink: 0;

  backdrop-filter:
    blur(20px);

  -webkit-backdrop-filter:
    blur(20px);

  border-top:
    1px solid
    rgba(
      255,
      255,
      255,
      0.08
    );
}

/* ========================================================= */
/* DESKTOP HEIGHT ADAPTATION */
/* ========================================================= */

@media (
  max-height: 900px
) and (
  min-width: 769px
) {
  .login-content {
    padding-top: 68px;
    padding-bottom: 18px;
  }

  .login-logo {
    transform:
      translateY(28px);
  }
}

@media (
  max-height: 820px
) and (
  min-width: 769px
) {
  .login-content {
    padding-top: 42px;
    padding-bottom: 16px;
  }

  .login-logo {
    transform:
      translateY(18px);
  }
}

@media (
  max-height: 740px
) and (
  min-width: 769px
) {
  .login-content {
    padding-top: 24px;
    padding-bottom: 14px;
  }

  .login-logo {
    transform:
      translateY(10px);
  }
}

/* ========================================================= */
/* TABLET */
/* ========================================================= */

@media (
  max-width: 768px
) {
  .login-page {
    height: 100vh;
    height: 100dvh;

    min-height: 100vh;
    min-height: 100dvh;
  }

  .login-background {
    background-position:
      center top;

    padding-top:
      calc(
        18px +
        env(
          safe-area-inset-top
        )
      );
  }

  .login-logo {
    width: 140px;

    transform:
      translateY(-12px);
  }

  .login-content {
    min-height: 0;

    align-items:
      flex-start;

    padding:
      70px
      16px
      18px;
  }

  .login-button-container {
    width: 340px;

    max-width:
      calc(
        100vw -
        32px
      );

    height: auto;

    margin-top: 0;
  }
}

/* ========================================================= */
/* MOBILE */
/* ========================================================= */

@media (
  max-width: 430px
) {
  .login-content {
    padding-top: 54px;
    padding-bottom: 12px;
  }

  .login-logo {
    width: 126px;
  }

  .login-footer {
    display: none;
  }
}

/* ========================================================= */
/* LOADING */
/* ========================================================= */

.loading-overlay {
  position: fixed;

  inset: 0;

  z-index: 100000;

  display: flex;

  align-items: center;
  justify-content: center;

  background:
    rgba(
      0,
      0,
      0,
      0.72
    );

  backdrop-filter:
    blur(6px);

  -webkit-backdrop-filter:
    blur(6px);
}

.loading-container {
  width: 220px;

  min-height: 150px;

  display: flex;

  flex-direction: column;

  align-items: center;
  justify-content: center;

  padding:
    28px 24px;

  border-radius: 22px;

  background:
    rgba(
      28,
      28,
      28,
      0.86
    );

  box-shadow:
    0 20px 60px
    rgba(
      0,
      0,
      0,
      0.35
    );

  animation:
    loadingPop
    0.22s ease;
}

.spinner {
  width: 42px;
  height: 42px;

  border-radius: 50%;

  border:
    4px solid
    rgba(
      255,
      255,
      255,
      0.22
    );

  border-top-color:
    #ffffff;

  animation:
    spin
    0.75s
    linear
    infinite;
}

.loading-text {
  margin-top: 16px;
  margin-bottom: 0;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 14px;

  color: white;

  text-align: center;
}

/* ========================================================= */
/* ANIMATIONS */
/* ========================================================= */

@keyframes spin {
  to {
    transform:
      rotate(360deg);
  }
}

@keyframes loadingPop {
  from {
    opacity: 0;

    transform:
      scale(0.96)
      translateY(6px);
  }

  to {
    opacity: 1;

    transform:
      scale(1)
      translateY(0);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity
    0.22s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ========================================================= */
/* MOBILE LOADING */
/* ========================================================= */

@media (
  max-width: 430px
) {
  .loading-container {
    width: 190px;

    min-height: 136px;

    border-radius: 20px;
  }

  .spinner {
    width: 38px;
    height: 38px;
  }

  .loading-text {
    font-size: 13px;
  }
}

/* ========================================================= */
/* BACK BUTTON */
/* ========================================================= */

.back-button {
  position: absolute;

  top: 22px;
  left: 22px;

  z-index: 20;

  width: 42px;
  height: 42px;

  display: flex;

  align-items: center;
  justify-content: center;

  padding: 0;

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.14
    );

  border-radius: 50%;

  color:
    rgba(
      255,
      255,
      255,
      0.92
    );

  background:
    linear-gradient(
      180deg,
      rgba(
        255,
        255,
        255,
        0.14
      ),
      rgba(
        255,
        255,
        255,
        0.07
      )
    );

  box-shadow:
    inset
    0 1px 0
    rgba(
      255,
      255,
      255,
      0.16
    ),
    0 8px 24px
    rgba(
      0,
      0,
      0,
      0.14
    );

  backdrop-filter:
    blur(18px);

  -webkit-backdrop-filter:
    blur(18px);

  cursor: pointer;

  transition:
    transform
    0.2s ease,
    background
    0.2s ease,
    border-color
    0.2s ease,
    box-shadow
    0.2s ease;
}

.back-button:hover {
  transform:
    translateY(-1px);

  border-color:
    rgba(
      255,
      255,
      255,
      0.24
    );

  background:
    linear-gradient(
      180deg,
      rgba(
        255,
        255,
        255,
        0.2
      ),
      rgba(
        255,
        255,
        255,
        0.1
      )
    );

  box-shadow:
    inset
    0 1px 0
    rgba(
      255,
      255,
      255,
      0.2
    ),
    0 12px 30px
    rgba(
      0,
      0,
      0,
      0.18
    );
}

.back-button:active {
  transform:
    scale(0.94);
}

.back-button:focus-visible {
  outline:
    2px solid
    rgba(
      40,
      135,
      247,
      0.7
    );

  outline-offset:
    3px;
}

[data-theme="light"]
.back-button {
  color:
    rgba(
      17,
      17,
      17,
      0.86
    );

  border-color:
    rgba(
      17,
      17,
      17,
      0.08
    );

  background:
    linear-gradient(
      180deg,
      rgba(
        255,
        255,
        255,
        0.82
      ),
      rgba(
        255,
        255,
        255,
        0.56
      )
    );

  box-shadow:
    inset
    0 1px 0
    rgba(
      255,
      255,
      255,
      0.9
    ),
    0 8px 24px
    rgba(
      0,
      0,
      0,
      0.08
    );
}

[data-theme="light"]
.back-button:hover {
  border-color:
    rgba(
      17,
      17,
      17,
      0.14
    );

  background:
    linear-gradient(
      180deg,
      rgba(
        255,
        255,
        255,
        0.96
      ),
      rgba(
        255,
        255,
        255,
        0.72
      )
    );
}

/* ========================================================= */
/* BACK BUTTON TABLET */
/* ========================================================= */

@media (
  max-width: 768px
) {
  .back-button {
    top:
      calc(
        18px +
        env(
          safe-area-inset-top
        )
      );

    left: 16px;

    width: 40px;
    height: 40px;
  }
}

/* ========================================================= */
/* BACK BUTTON MOBILE */
/* ========================================================= */

@media (
  max-width: 430px
) {
  .back-button {
    top:
      calc(
        14px +
        env(
          safe-area-inset-top
        )
      );

    left: 14px;

    width: 38px;
    height: 38px;
  }
}

/* ========================================================= */
/* REDUCED MOTION */
/* ========================================================= */

@media (
  prefers-reduced-motion:
    reduce
) {
  .back-button {
    transition: none;
  }

  .spinner {
    animation: none;
  }

  .loading-container {
    animation: none;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: none;
  }
}
</style>